import React from 'react';
import { Sparkles, Heart, Users, BookOpen, Video, ArrowRight, ListChecks } from 'lucide-react';

/* ====== PATTERN & DECO BACKGROUND ====== */
const PatternLayer = () => {
  // Pola garis miring halus (SVG) — aman & ringan
  const pattern = encodeURIComponent(`
    <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>
      <defs>
        <pattern id='p' width='32' height='32' patternUnits='userSpaceOnUse' patternTransform='rotate(20)'>
          <path d='M0 16 H32' stroke='rgba(0,0,0,0.06)' stroke-width='1'/>
        </pattern>
      </defs>
      <rect width='100%' height='100%' fill='url(#p)'/>
    </svg>
  `);

  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      {/* Layer 1: vertical gradient pakai palette-mu */}
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--caramel)]/25 via-[var(--arylide-yellow)]/18 to-transparent" />
      {/* Layer 2: radial glow lembut */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full blur-3xl opacity-40"
           style={{ background: 'radial-gradient(50% 50% at 50% 50%, var(--uc-gold) 0%, transparent 60%)' }} />
      {/* Layer 3: pola svg halus */}
      <div className="absolute inset-0"
           style={{ backgroundImage: `url("data:image/svg+xml;utf8,${pattern}")` }} />
      {/* Layer 4: motif “Mata Andau” sebagai blob */}
      <div className="absolute bottom-[-120px] right-[-120px] h-[420px] w-[420px] rounded-full blur-2xl opacity-30"
           style={{ background: 'radial-gradient(40% 40% at 50% 50%, var(--army-green) 0%, transparent 60%)' }} />
    </div>
  );
};

/* ====== DATA ====== */
const traditionalMotifs = [
  { name: 'Antang Tawit', meaning: 'soaring hope' },
  { name: 'Tajim Hepeng', meaning: 'blessings & abundance' },
  { name: 'Lambang Palangka', meaning: 'human origin' },
  { name: 'Suluh Petuk Danum', meaning: 'new life' },
  { name: 'Liau Baguti', meaning: 'the spirit realm' },
  { name: 'Burung Dahiang', meaning: 'misfortune flying away' },
  { name: 'Tunggul Balu', meaning: 'the soul’s final pause' },
  { name: 'Sawang', meaning: 'prosperity & honor' },
];

const lifeStages = [
  { name: 'Nahunan', desc: 'Birth ceremony' },
  { name: 'Penganten Mandai', desc: 'Marriage ritual' },
  { name: 'Guest Welcome', desc: 'Honoring distinguished visitors' },
  { name: 'Tiwah', desc: 'Death & ancestral rituals' },
  { name: 'Manalapas Bawin Kuwu', desc: 'Ancient seclusion rite' }
];

const innovationItems = [
  { title: '1. Motif Adaptation', description: 'Reimagined into geometric lines, botanical patterns, minimalist reinterpretations, and the Mata Andau modern motif.' },
  { title: '2. Nature-Based Material Revival', description: 'Reviving Humbang bamboo, Kajang, and Rattan, connecting modern craft to ancestral techniques.' },
  { title: '3. Weaver Regeneration Program', description: 'The “30 × 5” system: 30 youths trained, each trains 5 more, creating a sustainable lineage.' },
  { title: '4. AI & Technology for Craft', description: 'AI-powered pattern replication and material pre-processing tools for faster, authentic production.' },
  { title: '5. From Ritual to Art', description: 'Introducing Sapuyung Dare to fashion runways, exhibitions, museums, and as cultural diplomacy gifts.' },
  { title: '6. Digital Presence', description: 'Creating digital documentation via articles, high-quality photos, short films, and our heritage website.' },
  { title: '7. Product Innovation', description: 'New forms like wall installations, fashion collaborations, interior design pieces, and collector’s editions.' }
];

/* ====== PAGE ====== */
export const About = () => {
  return (
    <div className="relative min-h-screen pt-32 pb-24 font-body text-stone-700">
      <PatternLayer />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
        {/* Header */}
        <header className="text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-heading tracking-tight text-[var(--army-green)] mb-6">
            Our Story
          </h1>
          <p className="text-base sm:text-lg text-stone-700 max-w-2xl mx-auto leading-relaxed">
            Where ancestral wisdom meets modern innovation, preserving Dayak heritage one creation at a time.
          </p>
          <div className="w-20 h-1 bg-[var(--uc-gold)] mx-auto mt-8 rounded-full"></div>
        </header>

        {/* About card */}
        <section className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold font-heading tracking-tight text-[var(--army-green)]">About Marajaki Kreatif</h2>
            <div className="space-y-4 text-stone-700 text-base md:text-lg leading-relaxed">
              <p><strong>Marajaki Kreatif</strong> is a cultural brand focused on creating modern products based on traditional Dayak weaving, motifs, and innovation.</p>
              <p className="text-base">We are a cultural initiative dedicated to:</p>
              <ul className="space-y-2 list-disc list-outside ml-5 text-base text-stone-700">
                <li>Preserving Sapuyung Dare</li>
                <li>Innovating its motifs and applications</li>
                <li>Supporting local weavers and regenerating young artisans</li>
                <li>Documenting and reintroducing the object to the world</li>
                <li>Transforming ritual heritage into wearable art</li>
              </ul>
              <p className="font-semibold italic text-[var(--uc-gold)] pt-2">"We believe that culture survives when it evolves—without losing its soul."</p>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg">
            <img
              src="https://ik.imagekit.io/dandie/sapuyung%20dare/1.png"
              alt="Marajaki Kreatif Team"
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            />
          </div>
        </section>

        {/* Why matters */}
        <section className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 lg:order-first">
            <h2 className="text-3xl font-bold font-heading tracking-tight text-[var(--army-green)]">Why Sapuyung Dare Matters to Us</h2>
            <div className="space-y-4 text-stone-700 text-base md:text-lg leading-relaxed">
              <p>The sacred motifs of Sapuyung Dare are the primary source of our inspiration. This is not just a design choice; it is the core of our identity.</p>
              <p>We connect our <strong>brand identity</strong> directly to the <strong>ritual identity</strong> of the Sapuyung Dare, ensuring every product carries a story, a purpose, and a piece of ancestral wisdom.</p>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg lg:order-last">
            <img
              src="https://ik.imagekit.io/dandie/sapuyung%20dare/photo2.png?updatedAt=1762502575044"
              alt="Sapuyung Dare Inspiration"
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            />
          </div>
        </section>

        {/* Heritage */}
        <section className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 p-8 md:p-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold font-heading tracking-tight text-[var(--army-green)] mb-4">
              Sapuyung Dare: The Heritage Behind Our Designs
            </h2>
            <p className="text-xl font-medium text-[var(--uc-gold)] mb-10">A Ritual Hat That Lives With You - From Birth to Death</p>

            <div className="text-left bg-[var(--caramel)]/20 p-8 rounded-lg shadow-inner mb-10">
              <p className="text-stone-700 mb-6 text-lg">Sapuyung Dare is the only Dayak cultural object that accompanies a person through every stage of life:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {lifeStages.map((stage) => (
                  <div key={stage.name} className="flex items-start space-x-3">
                    <ListChecks className="w-5 h-5 text-[var(--uc-gold)] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold font-heading text-stone-800">{stage.name}</span>
                      <span className="text-sm text-stone-600 ml-2">- {stage.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-stone-600 mt-6 italic text-center">Its significance is profound—woven motifs serve as prayers, identity markers, and ancestral messages.</p>
            </div>
          </div>
        </section>

        {/* Motifs */}
        <section className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 p-8 md:p-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-heading tracking-tight text-[var(--army-green)] mb-4">Motifs of Sapuyung Dare</h2>
            <p className="text-lg text-stone-700 max-w-2xl mx-auto leading-relaxed">Each motif carries deep spiritual meaning and ancestral wisdom, connecting us to generations past.</p>
          </div>

          <div className="mb-12">
            <img
              src="https://ik.imagekit.io/dandie/sapuyung%20dare/sapuyung.zip%20-%209.png?updatedAt=1762467079262"
              alt="Sapuyung Dare Motifs"
              className="w-full max-w-4xl mx-auto rounded-xl shadow-lg"
            />
          </div>

          <h3 className="text-2xl font-semibold font-heading text-center mb-8 text-[var(--army-green)]">Traditional Motifs</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {traditionalMotifs.map((motif, index) => (
              <div key={index} className="p-6 rounded-xl bg-white/90 backdrop-blur border border-neutral-200 shadow-lg transition-all duration-300 hover:scale-[1.02]">
                <h3 className="text-lg font-semibold font-heading mb-2 text-[var(--uc-gold)] capitalize">{motif.name}</h3>
                <p className="text-sm text-stone-600 leading-6 capitalize">{motif.meaning}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 p-8 rounded-lg border-l-4 border-[var(--uc-gold)] bg-[var(--caramel)]/20 shadow-inner">
            <div className="flex items-start space-x-5">
              <Sparkles className="w-8 h-8 text-[var(--uc-gold)] flex-shrink-0 mt-1" />
              <div>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 bg-[var(--caramel)] text-[var(--uc-gold)]">Innovated Motif</span>
                <h3 className="text-2xl font-bold font-heading mb-3 text-[var(--army-green)]">Mata Andau</h3>
                <p className="text-stone-700 leading-relaxed">A modern reinterpretation inspired by the sun and horizon, symbolizing clarity, warmth, and continuity.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why it matters */}
        <section className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 p-8 md:p-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold font-heading tracking-tight text-[var(--army-green)] mb-4">Why Sapuyung Dare Deserves the World</h2>
            <p className="text-lg text-[var(--uc-gold)] font-medium">Rare. Ritualistic. Historically Connected Across Asia.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            {[
              { Icon: BookOpen, title: 'Only Asian Ritual Hat', desc: 'With meaningful motifs woven into every fiber' },
              { Icon: Heart, title: 'Living Artifact', desc: 'Of Austronesian migration and cultural evolution' },
              { Icon: Users, title: 'Nearly Extinct', desc: 'Only 3–5 elderly craftsmen remain in some regions' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-[var(--caramel)]/50">
                  <Icon className="w-8 h-8 text-[var(--uc-gold)]" />
                </div>
                <h3 className="text-lg font-semibold font-heading mb-2 text-[var(--army-green)]">{title}</h3>
                <p className="text-sm text-stone-600">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 mb-10 max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold font-heading mb-6 text-center text-[var(--army-green)]">By supporting Sapuyung Dare, you:</h3>
            <ul className="space-y-4">
              {[
                'Preserve Dayak cultural identity',
                'Protect endangered craftsmanship',
                'Give meaning to your fashion and home decor',
              ].map((t) => (
                <li key={t} className="flex items-start space-x-3">
                  <ListChecks className="w-6 h-6 text-[var(--uc-gold)] mt-1 flex-shrink-0" />
                  <span className="font-medium text-stone-800 text-base md:text-lg">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 rounded-lg bg-[var(--caramel)]/20 shadow-inner">
            <p className="text-center text-base md:text-lg font-semibold text-stone-800">This is more than a product—it’s cultural survival.</p>
          </div>
        </section>

        {/* Innovation */}
        <section className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 p-8 md:p-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-heading tracking-tight text-[var(--army-green)] mb-4">Innovation & Preservation</h2>
            <p className="text-xl text-[var(--uc-gold)] font-medium">Innovation IS Preservation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {innovationItems.map((item) => (
              <div key={item.title} className="p-6 rounded-xl bg-white/90 backdrop-blur border border-neutral-200 shadow-lg transition-all duration-300 hover:scale-[1.02]">
                <h3 className="text-lg font-semibold font-heading mb-3 text-[var(--army-green)]">{item.title}</h3>
                <p className="text-sm text-stone-600 leading-6">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Educational */}
        <section className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 py-12 px-6 md:px-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Video className="w-12 h-12 mx-auto mb-4 text-[var(--uc-gold)]" />
              <h2 className="text-3xl font-bold font-heading tracking-tight text-[var(--army-green)] mb-4">Educational Space</h2>
              <p className="text-lg text-stone-700 leading-relaxed">Your digital museum for Sapuyung Dare heritage and traditional weaving knowledge.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[var(--caramel)]/20 p-6 rounded-lg shadow-inner">
                <h3 className="text-lg font-semibold font-heading mb-4 text-[var(--uc-gold)]">Video Archive</h3>
                <ul className="space-y-2 text-sm text-stone-700 list-disc list-outside ml-5">
                  <li>Behind-the-scenes weaving process</li>
                  <li>Interviews with elder weavers</li>
                  <li>Mini documentary: The Life of a Ritual Hat</li>
                  <li>Motif explanation videos</li>
                </ul>
                <a
                  href="https://drive.google.com/drive/folders/1OYrY66TOG60Y3pdnLPSfxsEbXl4wXUvQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 mt-5 text-sm font-semibold text-[var(--uc-gold)] hover:text-[var(--rufous)] transition-colors"
                >
                  <span>Watch Documentary</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              <div className="bg-[var(--caramel)]/20 p-6 rounded-lg shadow-inner">
                <h3 className="text-lg font-semibold font-heading mb-4 text-[var(--uc-gold)]">Books & Articles</h3>
                <p className="text-sm text-stone-700 mb-4">Access references and research materials:</p>
                <ul className="space-y-2 text-sm text-stone-700 list-disc list-outside ml-5">
                  <li>Sapuyung: Cappelli Cerimoniali del Borneo (Maiullari, 2011)</li>
                  <li>Salakot and Other Headgear of Asia (Peralta, 2013)</li>
                  <li>Research on Dayak rituals, migration, and motifs</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-black/5 p-12">
            <h2 className="text-3xl font-bold font-heading tracking-tight text-[var(--army-green)] mb-6">This is More Than a Product</h2>
            <p className="text-lg text-stone-700 mb-8 max-w-2xl mx-auto leading-relaxed">It's cultural survival. Join us in preserving and celebrating Dayak heritage.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/products"
                className="inline-flex items-center justify-center space-x-2 px-8 py-3 rounded-lg font-semibold text-white bg-[var(--rufous)] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[var(--rufous)] focus:ring-offset-2"
              >
                <span>Explore Our Collection</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center space-x-2 px-8 py-3 rounded-lg font-semibold text-[var(--army-green)] bg-[var(--arylide-yellow)] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 hover:bg-[var(--uc-gold)] focus:outline-none focus:ring-2 focus:ring-[var(--uc-gold)] focus:ring-offset-2"
              >
                <span>Get in Touch</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
